import { AlertTriangle, Check, Info, Lightbulb } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/css";

export type InfoBlockVariant = "info" | "warning" | "tip";

interface InfoBlockProps {
  /** Visual variant — controls colour and default icon. */
  variant?: InfoBlockVariant;
  /** Lucide icon override. Defaults per variant: info→Info, warning→AlertTriangle, tip→Lightbulb. */
  icon?: LucideIcon;
  /** Optional i18n key for the heading. */
  titleKey?: string;
  /** Required i18n key for the body paragraph. */
  bodyKey: string;
  /** Optional i18n key resolving (via returnObjects) to a string[] for a bullet list. */
  itemsKey?: string;
  /** Additional className applied to the root container. */
  className?: string;
}

const VARIANT_CLASSES: Record<InfoBlockVariant, string> = {
  info: "bg-card text-card-foreground border-border",
  warning: "bg-card text-card-foreground border-amber-500/40",
  tip: "bg-card text-card-foreground border-emerald-500/40",
};

const VARIANT_ICON_CLASSES: Record<InfoBlockVariant, string> = {
  info: "text-muted-foreground",
  warning: "text-amber-600 dark:text-amber-400",
  tip: "text-emerald-600 dark:text-emerald-400",
};

const DEFAULT_ICON: Record<InfoBlockVariant, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  tip: Lightbulb,
};

export function InfoBlock({
  variant = "info",
  icon,
  titleKey,
  bodyKey,
  itemsKey,
  className,
}: InfoBlockProps) {
  const { t } = useTranslation();
  const IconComponent = icon ?? DEFAULT_ICON[variant];
  const rawItems = itemsKey
    ? (t(itemsKey, { returnObjects: true }) as unknown)
    : undefined;
  const items = Array.isArray(rawItems) ? rawItems : undefined;

  return (
    <div
      className={cn(
        "flex gap-3 rounded-lg border p-4",
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      <IconComponent
        className={cn("size-5 shrink-0 mt-0.5", VARIANT_ICON_CLASSES[variant])}
      />
      <div className="flex flex-col gap-2 min-w-0">
        {titleKey && <p className="text-sm font-medium">{t(titleKey)}</p>}
        <p className="text-sm text-muted-foreground">{t(bodyKey)}</p>
        {items && items.length > 0 && (
          <ul className="flex flex-col gap-1.5 mt-1">
            {items.map((item) => (
              <li
                key={String(item)}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <Check
                  className={cn(
                    "size-4 shrink-0 mt-0.5",
                    VARIANT_ICON_CLASSES[variant],
                  )}
                />
                <span>{String(item)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
